'use client';

import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { env } from '@repo/platform-env';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useAnswerDispatch } from '@/entities/answer/model/hooks';
import type { SessionSyncResult } from '@/entities/session/api/sessionWriteResult';
import { isFunnelComplete } from '@/shared/config/funnel/completion';
import type { FlattenedFunnelConfig } from '@/shared/config/funnel/flattened/types';
import { saveFunnelSession } from '../api/saveFunnelSession';
import { hasAuthTokenCookie } from '../lib/useHasAuthTokenCookie';
import { abandonFunnelRun } from './abandonFunnelRun';
import { markLocalSessionComplete, readLocalSession } from './localSession';
import { resolveCompleteAction } from './resolveCompleteAction';

/**
 * Commits a finished funnel run — the only path that marks a run complete.
 *
 * Callers pass the answers in: the end-of-funnel caller's store snapshot
 * predates the final `SUBMIT_ANSWER` dispatch.
 *
 * Awaited by `useFinishFunnel`, so the run is marked complete locally before
 * the results page mounts and reads it.
 *
 * Never gated on being logged in — anonymous runs are recorded too.
 */
export function useCompleteFunnelRun(): (
  funnelFlattenedConfig: FlattenedFunnelConfig,
  funnelConfigVersion: string,
  answers: AnswerState,
) => Promise<SessionSyncResult> {
  const dispatch = useAnswerDispatch();
  const router = useRouter();

  return useCallback(
    async (
      funnelFlattenedConfig: FlattenedFunnelConfig,
      funnelConfigVersion: string,
      answers: AnswerState,
    ): Promise<SessionSyncResult> => {
      const action = resolveCompleteAction({
        isRunComplete: isFunnelComplete(funnelFlattenedConfig, answers),
        localSession: readLocalSession(),
        sessionType: hasAuthTokenCookie() ? 'authenticated' : 'anonymous',
        isPersistenceEnabled: env.NEXT_PUBLIC_ENABLE_SESSION_PERSISTENCE,
      });

      if (action.kind === 'skip') return 'skipped';

      const { result, sessionId } = await saveFunnelSession(answers, {
        status: 'complete',
        funnelConfigVersion,
      });
      if (sessionId && (result === 'ok' || result === 'already-complete')) {
        markLocalSessionComplete(sessionId, action.attemptedAs);
      }
      // These answers belong to a funnel config that is no longer served.
      if (result === 'version-mismatch') abandonFunnelRun(dispatch, router);
      return result;
    },
    [dispatch, router],
  );
}
